'use client';

import * as React from 'react';
import { AlertCircle } from 'lucide-react';
import { Input } from './Input';
import { Label } from '@/shadcn/components/ui/label';
import { cn } from '@/shadcn/lib/utils';
import { Field } from '@/shadcn/components/ui/field';

type FormInputProps = Omit<React.ComponentProps<typeof Input>, 'id'> & {
    id: string;
    label?: React.ReactNode;
    required?: boolean;
    error?: React.ReactNode;
    hint?: React.ReactNode;
    wrapperClassName?: string;
};

export default function FormInput({
    id,
    label,
    required,
    error,
    hint,
    className,
    wrapperClassName,
    ...inputProps
}: FormInputProps) {
    const hasError = Boolean(error);
    const fieldName = inputProps.name ?? id;
    return (
        <Field className={cn(wrapperClassName)}>
            {label ? (
                <Label htmlFor={id} className="text-[14px] -mb-[5px] font-[400]">
                    {label}
                    {required ? ' *' : null}
                </Label>
            ) : null}

            <Input
                id={id}
                aria-invalid={hasError}
                aria-describedby={hasError ? `${id}-error` : hint ? `${id}-hint` : undefined}
                className={cn(
                    hasError && 'border-destructive focus-visible:ring-destructive',
                    className,
                )}
                {...inputProps}
                name={fieldName}
            />

            {hasError ? (
                <div
                    id={`${id}-error`}
                    className="flex items-center gap-2 text-[12px] text-destructive -mt-[7px]"
                >
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{error}</span>
                </div>
            ) : hint ? (
                <p id={`${id}-hint`} className="text-[12px] text-muted-foreground">
                    {hint}
                </p>
            ) : null}
        </Field>
    );
}
